import { ActionContext } from "vuex";
import { LivechatState } from "@/store/modules/livechat/types";
import { FlowEditorState } from "./modules/floweditor/types";
import { WhatsAppState } from "./modules/whatsapp/types";
import { FaqState } from "./modules/faq/types";
import { DepartmentState } from "./modules/department/types";
import { ApiEditorState } from "./modules/apieditor/types";
import { IFaq } from "@/helperMethods/faqType";
import { BroadcastState } from "./modules/broadcast/types";
import { EditorState } from "./modules/editor/types";
import { PrismState } from "./modules/prism/types";
import { OrderUpdate, TrendingType } from "@/helperMethods/trendingTopics/type";

export type CardType = {
  text?: string | string[];
  subtext: string;
  image?: string;
  url?: string;
  buttons?: any[];
  options?: any;
};

export interface ButtonType {
  text: string;
  event: string;
  data: any;
  phone: string;
  url: string;
  type: string;
}

export interface change {
  date_created: string;
  last_modified: string;
  status: string;
  type: string;
}

export interface NodeCondition {
  property: string;
  key?: string;
  operator?: "=" | ">" | "<" | "NOT" | "INCLUDES";
  logicalOperator?: "AND" | "OR";
  valueType?: "STRING" | "NUMBER" | "BOOLEAN" | "JSON";
  value: string;
}

export type ContentNodeType = Readonly<{
  id?: string;
  event?: string | boolean;
  conditions?: NodeCondition[];
  content: CardType | CardType[];
  quickReplies?: ButtonType[];
  next?: string;
  delay?: number;
  end?: boolean;
  nextSteps?: [
    {
      questionId: string;
    }
  ];
  options?: {
    disableTextInput: boolean;
    quickReplyDisplayMode?: QuickReplyDisplayMode;
    feedbackCollectionMode?: FeedbackCollectionMode;
    buttonDisplayMode?: ButtonDisplayMode;
  };
}>;

type QuickReplyDisplayMode = "default" | "always" | "none";
type FeedbackCollectionMode = "default" | "allow" | "disallow";
type ButtonDisplayMode = "default" | "always" | "disableIfNotLast";

export const ButtonDisplayMode = {
  DEFAULT: "default",
  ALWAYS: "always",
  DISABLE_IF_NOT_LAST: "disableIfNotLast",
} as const;

// FAQ V1
// FIXME: to be removed soon once every client moves to FAQv2
interface Example {
  text: string;
}
export interface FAQ {
  id: string;
  question: string;
  answer: string;
  variations: Example[];
  RowKey: number;
  enabled: boolean;
  tags: string[];

  tagInputVisible: boolean;
  tagInputValue: string;
}

export interface Score {
  score: Number;
  true: Number;
}

export interface Training {
  intentTestingCompleted: Boolean;
  unlabeled: Array<string>;
  labeled: Array<string>;
  dataset: IFaq[];
  score: Score;
  trainingProgress: number;
  all: Array<FAQ>;
  searched: Array<FAQ>;
}

export interface AuthO {
  users: Array<any>;
  usersStatic: Array<any>;
}

export interface Nodes {
  [type: string]: any;
  content: { [id: string]: ContentNodeType };
  trigger: any;
  condition: any;
  keywords: any;
  event: any[];
  tasks: any;
  changes: any[]; // Snapshots
}

export interface Search {
  content: Object | null;
  event: Object | null;
  entity: Object | null;
  api_mappings: Object | null;
}

export interface Profile {
  email: string;
  app_metadata: any;
  "https://login.keyreply.com/email": string;
  "https://login.keyreply.com/permissions": string[];
  "https://login.keyreply.com/roles": string[];
}

export interface RootState {
  profile: Profile;
  server: string;
  serverStatus: Object;
  bots: Array<string>;
  greeting: any;
  persistentMenu: Array<any>;
  filler: Array<any>;
  users: Object;
  loaded: Number;
  audit: Array<any>;
  activeNodeType: string;
  activeNodeId: string;
  activeNodeSelected: boolean;
  conditions: boolean;
  userInteractions: Array<any>;
  brain: string;
  account: string;
  posts: Object | any;
  broadcasts: Object;
  broadcast: BroadcastState;
  showEditor: boolean;
  showAdvanced: boolean;
  showDeveloperConsole: boolean;
  showIntentSelector: boolean;
  selectedIntent: string;
  modules: any;
  state?: any;
  botSnapshots?: any;
  activeNodeHistoryType?: string;
  agents: string[];
  auth0: AuthO;
  nodeNavigationStack: Array<any>;
  search: Search;
  analytics: any;
  socket: any;
  isPreview: Boolean;
  watson: Object;
  livechats: Array<any>;
  nodes: Nodes | any;
  contentMissingDetails: Array<Nodes>;
  intentMissingDetails: Array<any>;
  duplicateTriggers: Array<any>;
  wildcardTriggers: Array<any>;
  missingContentExpiry: string;
  identicalTriggerExpiry: string;
  missingIntentExpiry: string;
  // Modules
  training: Training;
  livechat: LivechatState;
  floweditor: FlowEditorState;
  apieditor: ApiEditorState;
  whatsapp: WhatsAppState;
  faq: FaqState;
  department: DepartmentState;
  editor: EditorState;
  prism: PrismState;
  visualizationData: any;
  processingVisualization: Boolean;
  releaseId: {
    dashboard: string;
    server: string;
  };
  currentTabLoggedOut: boolean;

  // trending
  trendingTopics: TrendingType[];
  currentlyTopics: TrendingType[];
  previousTopics: TrendingType[];
  filterDate: Date[];
  orderCurrently: OrderUpdate[];
}

// TODO: remove when new vuex release exports type def for ActionHandler
export type ActionHandler<S, R> = (injectee: ActionContext<S, R>, payload: any) => any;
export type Getter<S, R> = (state?: S, getters?: any, rootState?: R, rootGetters?: any) => any;
